import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const NAV = [
  { to: "/services", label: "Services" },
  { to: "/our-standards", label: "Our Standards" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/for-nannies", label: "For Nannies" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

const Wordmark = ({ onClick }) => (
  <Link
    to="/"
    onClick={onClick}
    data-testid="brand-wordmark"
    className="flex flex-col leading-none"
    aria-label="Nanny Poppins home"
  >
    <span className="font-serif text-xl tracking-tight text-[#0B0A08]">Nanny Poppins</span>
    <span className="mt-0.5 text-[0.6rem] font-semibold uppercase tracking-[0.32em] text-[#C6A04A]">
      United Arab Emirates
    </span>
  </Link>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const linkClass = ({ isActive }) =>
    `text-sm font-medium transition-colors hover:text-[#C6A04A] ${
      isActive ? "text-[#C6A04A]" : "text-[#0B0A08]/75"
    }`;

  return (
    <header
      className={`sticky top-0 z-40 border-b transition-all ${
        scrolled
          ? "border-[#0B0A08]/10 bg-[#F7F4ED]/90 backdrop-blur-md"
          : "border-transparent bg-[#F7F4ED]"
      }`}
    >
      <div className="container-page flex h-20 items-center justify-between gap-4">
        <Wordmark />

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} className={linkClass} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Link
            to="/request-a-nanny-match"
            data-testid="nav-request-match-cta"
            className="inline-flex items-center rounded-full bg-[#0B0A08] px-5 py-2.5 text-sm font-semibold text-[#F7F4ED] transition-colors hover:bg-[#0B0A08]/85"
          >
            Request a Nanny Match
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-expanded={open}
          aria-controls="mobile-menu"
          aria-label={open ? "Close menu" : "Open menu"}
          className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#0B0A08]/15 text-[#0B0A08] lg:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div
          id="mobile-menu"
          data-testid="mobile-menu"
          className="border-t border-[#0B0A08]/10 bg-[#F7F4ED] lg:hidden"
        >
          <nav className="container-page flex flex-col gap-1 py-4" aria-label="Mobile">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className="rounded-lg px-3 py-3 text-base font-medium text-[#0B0A08]/80 hover:bg-[#0B0A08]/5"
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/request-a-nanny-match"
              data-testid="mobile-nav-request-match-cta"
              className="mt-2 inline-flex items-center justify-center rounded-full bg-[#0B0A08] px-5 py-3 text-base font-semibold text-[#F7F4ED]"
            >
              Request a Nanny Match
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};
